/**
 * SettingsAdvancedController — onglet « Avancé » de /parametres.html
 * (EPIC-004 / 4.6).
 *
 * Pilote le bloc « Détection mauvais payeur » (deux seuils :
 * `badPayerMaxAvgDelay` jours, `badPayerMaxLatePct` %).
 *
 * Hydratation initiale : `GET /organization/settings`. Sauvegarde
 * via `PUT /organization/settings/payment-conditions` (les seuils
 * mauvais payeur sont stockés dans la même table `organization_settings`).
 *
 * Convention CLAUDE.md respectée :
 *   - Pas d'HTML inline en JS : résumé rendu via `<template>`.
 *   - Aucune classe CSS inventée.
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

export default class SettingsAdvancedController extends BaseController {
  static targets = [
    "blockBadPayer",
    "badPayerSummary",
    "delaiMoyenInput",
    "pctRetardInput",
    "badPayerSubmit",
  ];

  state = {
    badPayerMaxAvgDelay: 30,
    badPayerMaxLatePct: 50,
  };

  async connect() {
    try {
      const data = await api.getSettings();
      if (data && data.paymentConditions) {
        this._hydrateFromState(data.paymentConditions);
      }
    } catch (_error) {
      this.showError("Impossible de charger les seuils mauvais payeur.");
    }
  }

  // ---------------------------------------------------------------------------
  // edit-block : open / cancel
  // ---------------------------------------------------------------------------

  openBlock(event) {
    event.preventDefault();
    const block = event.currentTarget.closest("[data-edit-block]");
    if (!block) return;
    this._open(block);
  }

  cancelBlock(event) {
    event.preventDefault();
    const block = event.currentTarget.closest("[data-edit-block]");
    if (!block) return;
    this._populateInputs(this.state);
    this._close(block);
  }

  // ---------------------------------------------------------------------------
  // Hydration UI
  // ---------------------------------------------------------------------------

  _hydrateFromState(paymentConditions) {
    this.state = {
      badPayerMaxAvgDelay: Number(paymentConditions.badPayerMaxAvgDelay ?? 30),
      badPayerMaxLatePct: Number(paymentConditions.badPayerMaxLatePct ?? 50),
    };
    this._populateInputs(this.state);
    this._renderBadPayerSummary();
  }

  _populateInputs(state) {
    if (this.hasDelaiMoyenInputTarget) {
      this.delaiMoyenInputTarget.value = String(state.badPayerMaxAvgDelay);
    }
    if (this.hasPctRetardInputTarget) {
      this.pctRetardInputTarget.value = String(state.badPayerMaxLatePct);
    }
  }

  _renderBadPayerSummary() {
    if (!this.hasBadPayerSummaryTarget) return;
    const fragment = this.cloneTemplate("tpl-advanced-bad-payer-summary");
    this.fillSlots(fragment, {
      delai: `≥ ${this.state.badPayerMaxAvgDelay} j`,
      pct: `≥ ${this.state.badPayerMaxLatePct} %`,
    });
    while (this.badPayerSummaryTarget.firstChild) {
      this.badPayerSummaryTarget.removeChild(this.badPayerSummaryTarget.firstChild);
    }
    this.badPayerSummaryTarget.appendChild(fragment);
  }

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  async saveBadPayer(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const avgDelay = this._readInt(this.hasDelaiMoyenInputTarget ? this.delaiMoyenInputTarget : null, 0, 180);
    const pctLate = this._readInt(this.hasPctRetardInputTarget ? this.pctRetardInputTarget : null, 0, 100);
    if (avgDelay === null || pctLate === null) {
      this.showError("Seuils invalides : délai 0-180 j, % retard 0-100 %.");
      return;
    }

    if (this.hasBadPayerSubmitTarget) this.badPayerSubmitTarget.disabled = true;
    try {
      const data = await api.updatePaymentConditions({
        badPayerMaxAvgDelay: avgDelay,
        badPayerMaxLatePct: pctLate,
      });
      if (data && data.paymentConditions) {
        this._hydrateFromState(data.paymentConditions);
      }
      if (this.hasBlockBadPayerTarget) this._close(this.blockBadPayerTarget);
      this.showSuccess("Seuils mauvais payeur enregistrés.");
    } catch (error) {
      const msg = error && error.message
        ? error.message
        : "Erreur lors de la mise à jour des seuils mauvais payeur.";
      this.showError(msg);
    } finally {
      if (this.hasBadPayerSubmitTarget) this.badPayerSubmitTarget.disabled = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _readInt(input, min, max) {
    if (!input) return null;
    const raw = Number.parseInt(input.value, 10);
    if (!Number.isFinite(raw) || raw < min || raw > max) return null;
    return raw;
  }

  _open(block) {
    block.dataset.editState = "editing";
    const summary = block.querySelector("[data-edit-summary]");
    const form = block.querySelector("[data-edit-form]");
    if (summary) summary.hidden = true;
    if (form) {
      form.hidden = false;
      const first = form.querySelector("input:not([type='hidden']), select, textarea");
      if (first) first.focus();
    }
  }

  _close(block) {
    block.dataset.editState = "idle";
    const summary = block.querySelector("[data-edit-summary]");
    const form = block.querySelector("[data-edit-form]");
    if (form) form.hidden = true;
    if (summary) summary.hidden = false;
  }
}
